import { Injectable } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from "@angular/common/http";
import { catchError, filter, finalize, switchMap, take, map } from 'rxjs/operators';
import { Observable, Subject, throwError, of } from "rxjs";

import { environment } from "../../../../environments/environment";
import { StorageService } from "../storage.service";
import { Config } from "../../Config";
import { TokenService } from "../../access-control/token.service";
import { AuthService } from '../../auth/auth.service';

interface HeaderStrategy {
	/** matches determine if the url meets a condition. If it does, the headers function alongside is used */
	matches: (url: string) => boolean;
	headers: (req: HttpRequest<any>) => HttpHeaders | Observable<HttpHeaders>;
}

@Injectable({
	providedIn: "root",
})
export class InterceptorService implements HttpInterceptor {
	refreshTokenInProgress = false;
	tokenSubject = new Subject<string | null>();

	private readonly headerStrategies: HeaderStrategy[] = [
		{
			matches: (url) => this.isAuthEndpoint(url),
			headers: () => new HttpHeaders().append("Accept", "application/json")
		},
		{
			matches: (url) => url.includes("graph.microsoft.com"),
			headers: () => new HttpHeaders({
				Accept: "application/json",
				Authorization: `Bearer ${this.storageService.retrieveOutlookToken()}`
			})
		},
		{
			matches: (url) => url.includes(this.config.computeSignature),
			headers: () => new HttpHeaders({
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: `Bearer ${this.storageService.getCurrentUser()?.token}`
				// Bearer: `${this.storageService.getCurrentUser()?.token}`
			})
		},
		{
			matches: (url) => url.includes(this.config.getDashboard),
			headers: (req) => this.getSecureHeaders(req, environment.BASE_URL)
		},
		{
			matches: (url) => url.includes('refresh-token'),
			headers: () => new HttpHeaders({
				Authorization: this.storageService.getCurrentUser()?.refreshToken
			})
		},
		{
			matches: (url) => url.includes('.json'),
			headers: () => new HttpHeaders().append("Accept", "application/json")
		},
		// Default strategy should be last
		{
			matches: () => true,
			headers: (req) => this.getSecureHeaders(req, environment.BASE_URL)
		},
	];

	constructor(
		private config: Config,
		private storageService: StorageService,
		private tokenService: TokenService,
		private authService: AuthService
	) { }

	private isAuthEndpoint(url: string): boolean {
		return [
			this.config.login,
			this.config.sendResetLink,
			this.config.setPassword,
			this.config.verifyUser
		].some(endpoint => url.includes(endpoint));
	}

	private getSecureHeaders(req: HttpRequest<any>, baseUrl: string): Observable<HttpHeaders> {
		// To re-enable compute signature, uncomment this block and remove the static fallback below.
		/*
		return this.tokenService.computeSignature(
			req.method,
			req.url.slice(baseUrl.length)
		).pipe(
			map((xToken) => {
				let headers = new HttpHeaders({
					Bearer: `${this.storageService.getCurrentUser()?.token}`,
					Nonce: environment.NONCE
				});
				if (xToken) {
					headers = headers.append('Signature', xToken.signature);
					headers = headers.append('X_TOKEN', xToken.accessToken);
					headers = headers.append('Timestamp', xToken.timestamp.toString());
				}
				return headers;
			}),
			catchError(() => of(new HttpHeaders({
				// Authorization: `Bearer ${this.storageService.getCurrentUser()?.token}`,
				Bearer: `${this.storageService.getCurrentUser()?.token}`,
				Nonce: environment.NONCE
			})))
		);
		*/
		// Static fallback headers for now, until compute signature is re-enabled
		return of(new HttpHeaders({
			Bearer: `${this.storageService.getCurrentUser()?.token}`,
			Nonce: environment.NONCE
		}));
	}

	intercept(
		req: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		const strategy = this.headerStrategies.find(s => s.matches(req.url));
		const headersResult = strategy?.headers(req);

		const headers$ = headersResult instanceof Observable
			? headersResult
			: of(headersResult ?? new HttpHeaders());

		return headers$.pipe(
			switchMap(headers => {
				const request = req.clone({ headers });
				return next.handle(request).pipe(
					catchError((error) => {
						if (error.status === 403) {
							if (request.url.includes(this.config.login)) {
								return throwError(error);
							}
							return this.handleUnauthorizedError(req, next);
						}
						return throwError(error);
					})
				);
			})
		);
	}

	handleUnauthorizedError(req: HttpRequest<any>, next: HttpHandler) {
		if (!this.refreshTokenInProgress) {
			this.refreshTokenInProgress = true;
			this.tokenSubject.next(null);
			return this.authService.refreshToken().pipe(
				switchMap((newUserData) => {
					this.storageService.storeCurrentUser(newUserData);
					this.tokenService.clearSignatureCache(); // Clear cached signatures to get new timestamps
					this.tokenSubject.next(newUserData.token); // trigger to run queued requests
					return this.addTokenToFailedRequest(req).pipe(
						switchMap(newReq => next.handle(newReq))
					);
				}),
				catchError((refreshError) => {
					this.tokenSubject.error(refreshError); // Terminate all queued requests safely
					this.tokenSubject = new Subject<string | null>(); // Recreate subject for future usage
					this.authService.logout();
					return throwError(refreshError);
				}),
				finalize(() => {
					this.refreshTokenInProgress = false; // Reset the flag
					if (!this.tokenSubject.closed) {
						this.tokenSubject.next(null);
					}
				}),
			)
		}
		// else block: Queue requests when token is being currently refreshed
		return this.tokenSubject.pipe(
			filter((token) => token != null),
			take(1),
			switchMap((_token) => this.addTokenToFailedRequest(req)),
			switchMap((newReq) => next.handle(newReq)),
			catchError(error => {
				this.authService.logout();
				return throwError(() => error);
			})
		);
	}

	addTokenToFailedRequest(req: HttpRequest<any>): Observable<HttpRequest<any>> {
		const strategy = this.headerStrategies.find(s => s.matches(req.url));
		const headersResult = strategy?.headers(req);
		const headers$ = headersResult instanceof Observable
			? headersResult
			: of(headersResult ?? new HttpHeaders());

		return headers$.pipe(map(headers => req.clone({ headers })));
	}
}
